import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { useTranslation } from 'react-i18next';
import { FirebaseContext } from 'common';
import RichTextEditor from './RichTextEditor.jsx';

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const asNumber = (value) => {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const clampInt = (value, min = 0) => {
  const n = Math.floor(Number(value || 0));
  if (!Number.isFinite(n)) return min;
  return Math.max(min, n);
};

const normalizeProduct = (product) => {
  const safe = product && typeof product === 'object' ? product : {};
  const translations = safe.translations && typeof safe.translations === 'object' ? safe.translations : {};
  const images = Array.isArray(safe.images) ? safe.images.filter(Boolean) : [];

  return {
    id: safe.id || '',
    type: safe.type === 'service' ? 'service' : 'product',
    title: safe.title || '',
    description: safe.description || '',
    category: safe.category || '',
    tags: safe.tags || '',
    sku: safe.sku || '',
    barcode: safe.barcode || '',
    price: asNumber(safe.price) ?? 0,
    compareAtPrice: asNumber(safe.compareAtPrice),
    stock: clampInt(safe.stock, 0),
    minPurchaseQty: clampInt(safe.minPurchaseQty, 1),
    image: safe.image || '',
    images,
    videoUrl: safe.videoUrl || '',
    videoThumbnail: safe.videoThumbnail || '',
    externalLink: safe.externalLink || '',
    externalLinkText: safe.externalLinkText || '',
    seo: {
      title: safe.seo?.title || '',
      description: safe.seo?.description || '',
      image: safe.seo?.image || '',
    },
    featured: Boolean(safe.featured),
    published: safe.published !== false,
    translations,
  };
};

const pruneTranslations = (translations) => {
  if (!translations || typeof translations !== 'object') return undefined;
  const out = {};
  Object.entries(translations).forEach(([locale, value]) => {
    if (!value || typeof value !== 'object') return;
    const cleaned = {};
    if (value.title) cleaned.title = value.title;
    if (value.description) cleaned.description = value.description;
    if (value.tags) cleaned.tags = value.tags;
    if (value.externalLinkText) cleaned.externalLinkText = value.externalLinkText;
    if (value.seo && typeof value.seo === 'object') {
      const seo = {};
      if (value.seo.title) seo.title = value.seo.title;
      if (value.seo.description) seo.description = value.seo.description;
      if (Object.keys(seo).length) cleaned.seo = seo;
    }
    if (Object.keys(cleaned).length) out[locale] = cleaned;
  });
  return Object.keys(out).length ? out : undefined;
};

const ImageUrlField = ({
  label,
  value,
  onChange,
  helperText,
  disabled,
  uploadPathPrefix = 'website_assets/storefront/shop',
  previewSize = 64,
}) => {
  const firebase = useContext(FirebaseContext);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [failed, setFailed] = useState(false);

  const hasValue = Boolean(value);
  const canUpload = Boolean(firebase?.storage) && !disabled;

  useEffect(() => {
    setFailed(false);
  }, [value]);

  const handleUploadClick = () => {
    if (!canUpload) return;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!firebase?.storage) {
      alert('Storage is not available yet.');
      return;
    }

    setUploading(true);
    try {
      const safeName = String(file.name || 'image')
        .replace(/[^a-z0-9._-]+/gi, '_')
        .slice(0, 80);
      const path = `${uploadPathPrefix}/${Date.now()}_${safeName}`;
      const ref = firebase.storage.ref(path);
      await ref.put(file);
      const url = await ref.getDownloadURL();
      onChange(url);
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleOpen = () => {
    if (!hasValue) return;
    window.open(value, '_blank', 'noopener,noreferrer');
  };

  const handleClear = () => onChange('');

  return (
    <Stack spacing={1}>
      <TextField
        fullWidth
        label={label}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        helperText={helperText}
        disabled={disabled}
      />

      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloudUploadRoundedIcon />}
            onClick={handleUploadClick}
            disabled={!canUpload || uploading}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewRoundedIcon />}
            onClick={handleOpen}
            disabled={!hasValue}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Open
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<LinkOffRoundedIcon />}
            onClick={handleClear}
            disabled={!hasValue || disabled}
            sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800 }}
          >
            Clear
          </Button>
        </Stack>

        <Box
          sx={{
            width: previewSize,
            height: previewSize,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            bgcolor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {hasValue && !failed ? (
            <Box
              component="img"
              alt=""
              src={value}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={() => setFailed(true)}
            />
          ) : (
            <ImageRoundedIcon sx={{ color: '#94a3b8' }} />
          )}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelected}
          style={{ display: 'none' }}
        />
      </Stack>
    </Stack>
  );
};

const SectionNav = ({ sections, active, onJump }) => (
  <List dense disablePadding sx={{ position: 'sticky', top: 24 }}>
    {sections.map((section) => (
      <ListItemButton
        key={section.key}
        selected={active === section.key}
        onClick={() => onJump(section.key)}
        sx={{ borderRadius: 2, mb: 0.5 }}
      >
        <ListItemText
          primary={section.label}
          secondary={section.description}
          primaryTypographyProps={{ fontWeight: 900, fontSize: 13 }}
          secondaryTypographyProps={{ fontSize: 12 }}
        />
      </ListItemButton>
    ))}
  </List>
);

export default function ShopProductEditorDialog({
  open,
  onClose,
  value,
  onSave,
  currency = 'USD',
  categories = [],
  languages = [],
  uploadPathPrefix = 'website_assets/storefront/shop',
  previewBaseUrl = '',
}) {
  const { t } = useTranslation();

  const languageTabs = useMemo(() => {
    const list = Array.isArray(languages) ? languages : [];
    const codes = list
      .map((lang) => ({ code: lang?.langLocale || lang?.id, label: lang?.langName }))
      .filter((item) => item.code);
    return [{ code: 'base', label: t('default') || 'Default' }, ...codes];
  }, [languages, t]);

  const [activeLocale, setActiveLocale] = useState('base');
  const [draft, setDraft] = useState(() => normalizeProduct(value));
  const [categoryInput, setCategoryInput] = useState('');
  const [expanded, setExpanded] = useState({
    basics: true,
    media: true,
    pricing: true,
    seo: false,
    publishing: true,
  });

  useEffect(() => {
    if (!open) return;
    const normalized = normalizeProduct(value);
    setActiveLocale('base');
    setDraft(normalized);
    setCategoryInput(normalized.category || '');
    setExpanded({
      basics: true,
      media: true,
      pricing: true,
      seo: false,
      publishing: true,
    });
  }, [open, value]);

  const categoryOptions = useMemo(() => {
    const list = Array.isArray(categories) ? categories : [];
    return list
      .map((cat) => (cat && typeof cat === 'object' ? { id: cat.id || '', title: cat.title || '' } : null))
      .filter((cat) => cat && cat.id)
      .sort((a, b) => String(a.title || a.id).localeCompare(String(b.title || b.id)));
  }, [categories]);

  const selectedCategory = useMemo(() => {
    if (!draft.category) return null;
    return categoryOptions.find((cat) => String(cat.id) === String(draft.category)) || null;
  }, [categoryOptions, draft.category]);

  useEffect(() => {
    if (!open) return;
    setCategoryInput(selectedCategory?.title || draft.category || '');
  }, [open, selectedCategory, draft.category]);

  const sectionRefs = {
    basics: useRef(null),
    media: useRef(null),
    pricing: useRef(null),
    seo: useRef(null),
    publishing: useRef(null),
  };

  const sections = useMemo(
    () => [
      { key: 'basics', label: t('basics') || 'Basics', description: 'Title, type, category, tags.' },
      { key: 'media', label: t('media') || 'Media', description: 'Images, gallery, video.' },
      { key: 'pricing', label: t('pricing') || 'Pricing + stock', description: 'Price, SKU, inventory.' },
      { key: 'seo', label: t('seo') || 'SEO', description: 'Meta title, description, share image.' },
      { key: 'publishing', label: t('publishing') || 'Publishing', description: 'Visibility and external links.' },
    ],
    [t]
  );

  const getTrans = (locale) => {
    if (!locale || locale === 'base') return {};
    const map = draft.translations && typeof draft.translations === 'object' ? draft.translations : {};
    return map[locale] && typeof map[locale] === 'object' ? map[locale] : {};
  };

  const setTrans = (locale, patch) => {
    if (!locale || locale === 'base') return;
    setDraft((prev) => {
      const current = prev.translations && typeof prev.translations === 'object' ? prev.translations : {};
      const existing = current[locale] && typeof current[locale] === 'object' ? current[locale] : {};
      return {
        ...prev,
        translations: {
          ...current,
          [locale]: { ...existing, ...patch },
        },
      };
    });
  };

  const getLocalizedValue = (field) => {
    if (activeLocale === 'base') return draft[field] || '';
    const trans = getTrans(activeLocale);
    return trans[field] ?? draft[field] ?? '';
  };

  const setLocalizedValue = (field, valueToSet) => {
    if (activeLocale === 'base') {
      setDraft((prev) => ({ ...prev, [field]: valueToSet }));
      return;
    }
    setTrans(activeLocale, { [field]: valueToSet });
  };

  const getLocalizedSeoValue = (field) => {
    if (activeLocale === 'base') return draft.seo?.[field] || '';
    const trans = getTrans(activeLocale);
    return trans.seo?.[field] ?? draft.seo?.[field] ?? '';
  };

  const setLocalizedSeoValue = (field, valueToSet) => {
    if (activeLocale === 'base') {
      setDraft((prev) => ({ ...prev, seo: { ...(prev.seo || {}), [field]: valueToSet } }));
      return;
    }
    const trans = getTrans(activeLocale);
    setTrans(activeLocale, {
      seo: { ...(trans.seo || {}), [field]: valueToSet },
    });
  };

  const handleJump = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: true }));
    const node = sectionRefs[key]?.current;
    if (!node) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setGalleryImage = (index, valueToSet) => {
    setDraft((prev) => {
      const list = Array.isArray(prev.images) ? [...prev.images] : [];
      list[index] = valueToSet;
      return { ...prev, images: list.filter((v) => v !== undefined) };
    });
  };

  const removeGalleryImage = (index) => {
    setDraft((prev) => {
      const list = Array.isArray(prev.images) ? [...prev.images] : [];
      list.splice(index, 1);
      return { ...prev, images: list };
    });
  };

  const moveGalleryImage = (index, direction) => {
    setDraft((prev) => {
      const list = Array.isArray(prev.images) ? [...prev.images] : [];
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= list.length) return prev;
      const tmp = list[index];
      list[index] = list[nextIndex];
      list[nextIndex] = tmp;
      return { ...prev, images: list };
    });
  };

  const commitCategoryInput = () => {
    const trimmed = String(categoryInput || '').trim();
    if (!trimmed) {
      setDraft((prev) => ({ ...prev, category: '' }));
      setCategoryInput('');
      return;
    }

    const match = categoryOptions.find(
      (cat) => String(cat.title || '').trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (match) {
      setDraft((prev) => ({ ...prev, category: match.id }));
      setCategoryInput(match.title || match.id);
      return;
    }

    // Free-form category: store as a slug so shop filters stay stable.
    setDraft((prev) => ({ ...prev, category: slugify(trimmed) }));
  };

  const handleSave = () => {
    const normalized = normalizeProduct(draft);
    const nextId = normalized.id || slugify(normalized.title) || `item-${Date.now()}`;
    const cleanedTranslations = pruneTranslations(normalized.translations);
    const payload = {
      ...normalized,
      id: nextId,
      price: asNumber(normalized.price) ?? 0,
      compareAtPrice: asNumber(normalized.compareAtPrice),
      stock: normalized.type === 'service' ? undefined : clampInt(normalized.stock, 0),
      minPurchaseQty: clampInt(normalized.minPurchaseQty, 1),
      tags: String(normalized.tags || ''),
      images: Array.isArray(normalized.images) ? normalized.images.filter(Boolean) : [],
      translations: cleanedTranslations,
    };
    if (payload.compareAtPrice === undefined) delete payload.compareAtPrice;
    if (!payload.images.length) delete payload.images;
    if (!payload.translations) delete payload.translations;
    if (!payload.videoUrl) delete payload.videoUrl;
    if (!payload.videoThumbnail) delete payload.videoThumbnail;
    if (!payload.externalLink) delete payload.externalLink;
    if (!payload.externalLinkText) delete payload.externalLinkText;

    onSave?.(payload);
  };

  const titleLabel = getLocalizedValue('title');
  const headline = titleLabel || (draft.id ? `Item: ${draft.id}` : (t('new_item') || 'New item'));
  const statusChip = draft.published ? (
    <Chip size="small" label={t('published') || 'Published'} color="success" sx={{ fontWeight: 900 }} />
  ) : (
    <Chip size="small" label={t('unpublished') || 'Unpublished'} sx={{ fontWeight: 900 }} />
  );
  const previewId = draft.id || slugify(draft.title);
  const previewHref =
    previewBaseUrl && previewId
      ? `${String(previewBaseUrl).replace(/\/+$/, '')}/${encodeURIComponent(previewId)}`
      : '';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontWeight: 950, fontSize: 18 }}>{headline}</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.25 }}>
              {(t('currency') || 'Currency') + `: ${currency}`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {statusChip}
            {previewBaseUrl ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewRoundedIcon />}
                onClick={() => window.open(previewHref, '_blank', 'noopener,noreferrer')}
                disabled={!previewHref}
                sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
              >
                {t('preview') || 'Preview'}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Tabs
            value={activeLocale}
            onChange={(_, valueToSet) => setActiveLocale(valueToSet)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {languageTabs.map((lang) => (
              <Tab key={lang.code} value={lang.code} label={lang.label || lang.code.toUpperCase()} />
            ))}
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Grid container spacing={0}>
          <Grid
            item
            xs={12}
            md={3}
            sx={{
              borderRight: { md: '1px solid #e5e7eb' },
              p: 2,
              bgcolor: '#fafafa',
            }}
          >
            <SectionNav sections={sections} active={null} onJump={handleJump} />
          </Grid>

          <Grid item xs={12} md={9} sx={{ p: 2 }}>
            <Stack spacing={1.5}>
              <Box ref={sectionRefs.basics} />
              <Accordion
                expanded={expanded.basics}
                onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, basics: nextExpanded }))}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 900 }}>{t('basics') || 'Basics'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('id') || 'ID'}
                        value={draft.id || ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, id: slugify(e.target.value) }))}
                        helperText={t('id_help') || 'Used in URLs. Leave empty to auto-generate from the title.'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label={t('type') || 'Type'}
                        value={draft.type || 'product'}
                        onChange={(e) => setDraft((prev) => ({ ...prev, type: e.target.value }))}
                      >
                        <MenuItem value="product">{t('product') || 'Product'}</MenuItem>
                        <MenuItem value="service">{t('service') || 'Service'}</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('title') || 'Title'}
                        value={getLocalizedValue('title')}
                        onChange={(e) => setLocalizedValue('title', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <RichTextEditor
                        label={t('description') || 'Description'}
                        value={getLocalizedValue('description')}
                        onChange={(valueToSet) => setLocalizedValue('description', valueToSet)}
                        placeholder={t('description') || 'Description'}
                        minHeight={180}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        freeSolo
                        options={categoryOptions}
                        value={selectedCategory}
                        inputValue={categoryInput}
                        onInputChange={(_, next) => setCategoryInput(next)}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option?.title || option?.id || '';
                        }}
                        isOptionEqualToValue={(option, valueToTest) =>
                          String(option?.id || '') === String(valueToTest?.id || '')
                        }
                        onChange={(_, nextValue) => {
                          if (!nextValue) {
                            setCategoryInput('');
                            setDraft((prev) => ({ ...prev, category: '' }));
                            return;
                          }
                          if (typeof nextValue === 'string') {
                            setCategoryInput(nextValue);
                            setDraft((prev) => ({ ...prev, category: slugify(nextValue) }));
                            return;
                          }
                          setCategoryInput(nextValue.title || nextValue.id || '');
                          setDraft((prev) => ({ ...prev, category: nextValue.id || '' }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            label={t('category') || 'Category'}
                            helperText={t('category_help') || 'Optional. Shown in filters and used for grouping.'}
                            onBlur={commitCategoryInput}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('tags') || 'Tags'}
                        value={getLocalizedValue('tags')}
                        onChange={(e) => setLocalizedValue('tags', e.target.value)}
                        helperText={t('tags_help') || 'Comma separated. Example: airport, vip, business'}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Box ref={sectionRefs.media} />
              <Accordion
                expanded={expanded.media}
                onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, media: nextExpanded }))}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 900 }}>{t('media') || 'Media'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <ImageUrlField
                      label={t('thumbnail_image') || 'Thumbnail image'}
                      value={draft.image}
                      onChange={(valueToSet) => setDraft((prev) => ({ ...prev, image: valueToSet }))}
                      uploadPathPrefix={uploadPathPrefix}
                    />

                    <Divider />

                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography sx={{ fontWeight: 900 }}>{t('gallery_images') || 'Gallery images'}</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddRoundedIcon />}
                          onClick={() => setDraft((prev) => ({ ...prev, images: [...(prev.images || []), ''] }))}
                          sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 900 }}
                        >
                          {t('add') || 'Add'}
                        </Button>
                      </Stack>

                      {Array.isArray(draft.images) && draft.images.length ? (
                        <Stack spacing={2}>
                          {draft.images.map((img, idx) => (
                            <Box
                              key={`gallery-${idx}`}
                              sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 1.5, bgcolor: '#fff' }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Typography variant="body2" sx={{ fontWeight: 900, color: '#111827' }}>
                                  {(t('image') || 'Image') + ` #${idx + 1}`}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <IconButton size="small" onClick={() => moveGalleryImage(idx, -1)} disabled={idx === 0}>
                                    <KeyboardArrowUpRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => moveGalleryImage(idx, 1)}
                                    disabled={idx === draft.images.length - 1}
                                  >
                                    <KeyboardArrowDownRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error" onClick={() => removeGalleryImage(idx)}>
                                    <DeleteRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Stack>
                              <Box sx={{ mt: 1 }}>
                                <ImageUrlField
                                  label={t('image_url') || 'Image URL'}
                                  value={img}
                                  onChange={(valueToSet) => setGalleryImage(idx, valueToSet)}
                                  uploadPathPrefix={uploadPathPrefix}
                                  previewSize={72}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#6b7280' }}>
                          {t('no_gallery_images') || 'No gallery images yet.'}
                        </Typography>
                      )}
                    </Stack>

                    <Divider />

                    <TextField
                      fullWidth
                      label={t('video_url') || 'Video URL'}
                      value={draft.videoUrl || ''}
                      onChange={(e) => setDraft((prev) => ({ ...prev, videoUrl: e.target.value }))}
                      helperText={t('video_url_help') || 'YouTube/Vimeo link (optional).'}
                    />
                    <ImageUrlField
                      label={t('video_thumbnail') || 'Video thumbnail'}
                      value={draft.videoThumbnail || ''}
                      onChange={(valueToSet) => setDraft((prev) => ({ ...prev, videoThumbnail: valueToSet }))}
                      uploadPathPrefix={uploadPathPrefix}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Box ref={sectionRefs.pricing} />
              <Accordion
                expanded={expanded.pricing}
                onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, pricing: nextExpanded }))}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 900 }}>{t('pricing') || 'Pricing + stock'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('price') || 'Price'}
                        value={draft.price ?? 0}
                        onChange={(e) => setDraft((prev) => ({ ...prev, price: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('compare_at') || 'Compare at'}
                        value={draft.compareAtPrice ?? ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, compareAtPrice: e.target.value }))}
                        helperText={t('compare_at_help') || 'Optional. Used for showing savings.'}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('sku') || 'SKU'}
                        value={draft.sku || ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, sku: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('barcode') || 'Barcode'}
                        value={draft.barcode || ''}
                        onChange={(e) => setDraft((prev) => ({ ...prev, barcode: e.target.value }))}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('minimum_purchase_qty') || 'Minimum purchase qty'}
                        value={draft.minPurchaseQty ?? 1}
                        onChange={(e) => setDraft((prev) => ({ ...prev, minPurchaseQty: e.target.value }))}
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label={t('quantity') || 'Quantity'}
                        value={draft.type === 'service' ? '' : draft.stock ?? 0}
                        onChange={(e) => setDraft((prev) => ({ ...prev, stock: e.target.value }))}
                        inputProps={{ min: 0 }}
                        disabled={draft.type === 'service'}
                        helperText={
                          draft.type === 'service'
                            ? (t('service_no_stock') || 'Services do not require stock.')
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <Box ref={sectionRefs.seo} />
              <Accordion expanded={expanded.seo} onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, seo: nextExpanded }))}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 900 }}>{t('seo') || 'SEO'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label={t('seo_title') || 'SEO title'}
                      value={getLocalizedSeoValue('title')}
                      onChange={(e) => setLocalizedSeoValue('title', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      label={t('seo_description') || 'SEO description'}
                      value={getLocalizedSeoValue('description')}
                      onChange={(e) => setLocalizedSeoValue('description', e.target.value)}
                      multiline
                      minRows={2}
                    />
                    <ImageUrlField
                      label={t('og_image') || 'OpenGraph image'}
                      value={draft.seo?.image || ''}
                      onChange={(valueToSet) =>
                        setDraft((prev) => ({ ...prev, seo: { ...(prev.seo || {}), image: valueToSet } }))
                      }
                      uploadPathPrefix={uploadPathPrefix}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>

              <Box ref={sectionRefs.publishing} />
              <Accordion
                expanded={expanded.publishing}
                onChange={(_, nextExpanded) => setExpanded((prev) => ({ ...prev, publishing: nextExpanded }))}
              >
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                  <Typography sx={{ fontWeight: 900 }}>{t('publishing') || 'Publishing'}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={draft.published !== false}
                          onChange={(_, checked) => setDraft((prev) => ({ ...prev, published: checked }))}
                        />
                      }
                      label={t('published') || 'Published'}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(draft.featured)}
                          onChange={(_, checked) => setDraft((prev) => ({ ...prev, featured: checked }))}
                        />
                      }
                      label={t('featured') || 'Featured'}
                    />

                    <Divider />

                    <TextField
                      fullWidth
                      label={t('external_link') || 'External link'}
                      value={draft.externalLink || ''}
                      onChange={(e) => setDraft((prev) => ({ ...prev, externalLink: e.target.value }))}
                      helperText={t('external_link_help') || 'Optional: send customers to another checkout URL.'}
                    />
                    <TextField
                      fullWidth
                      label={t('external_link_button_text') || 'External link button text'}
                      value={getLocalizedValue('externalLinkText')}
                      onChange={(e) => setLocalizedValue('externalLinkText', e.target.value)}
                    />
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
        >
          {t('save') || 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
